import Button from '@/components/Buttons/Button';
import { MaskedIcon } from '@/components/MaskedIcon/MaskedIcon';
import Restart from '@/images/Restart.svg';
import styled from '@emotion/styled';
import { Popover, Typography, TypographyProps } from '@mui/material';

const styles = {
    paper: {
        width: '372px',
        padding: '16px',
        boxShadow:
            '0.3px 0.8px 1.1px 0px rgba(187, 187, 187, 0.12), 1.1px 2.8px 3.9px -0.4px rgba(187, 187, 187, 0.17), 2.4px 6.1px 8.6px -0.8px rgba(187, 187, 187, 0.23), 5.3px 13.3px 18.8px -1.2px rgba(187, 187, 187, 0.29) !important',
        pointerEvents: 'auto',
        userSelect: 'none',
        borderRadius: '8px',
    },
};

const Title = styled(Typography)`
    font-weight: 600;
`;

const Capitalized = styled.span`
    text-transform: capitalize;
`;

const CharCountContainer = styled.div`
    border-bottom: 1px solid ${({ theme }) => theme.colors.black[300]};
    padding-top: 0.4rem;
    padding-bottom: 0.8rem;
    display: flex;
    justify-content: space-between;
    align-items: center;
`;

const CharCounts = styled.div`
    display: flex;
    align-items: center;
    gap: 0.4rem;
`;

const StyledBodyText = styled(Typography)`
    font-weight: 500;
    line-height: 2.4rem;
`;

const BodyText = (props: TypographyProps) => <StyledBodyText variant="body2" {...props} />;

const ContentText = styled(BodyText)`
    margin: 0.8rem 0;
`;

const GrayBodyText = styled(BodyText)`
    color: ${({ theme }) => theme.colors.black[700]};
`;

const ErrorBodyText = styled(BodyText)`
    color: ${({ theme }) => theme.colors.error[750]};
`;

const SuccessBodyText = styled(BodyText)`
    color: ${({ theme }) => theme.colors.success[750]};
`;

const CircleSeparator = styled.div`
    height: 0.4rem;
    width: 0.4rem;
    border-radius: 50%;
    margin: 1rem;
    background-color: ${({ theme }) => theme.colors.black[700]};
`;

const ResetButton = styled(Button)`
    font-size: 1.4rem;
    font-weight: 600;
    border-color: ${({ theme }) => theme.colors.black[300]};
    line-height: 2rem;
    height: fit-content;
    padding-left: 0.8rem;
    padding-right: 0.8rem;
`;

interface IProps {
    anchorEl: null | HTMLElement;
    open: boolean;
    onMouseEnter: () => void;
    onMouseLeave: () => void;
    onReset: () => void;
    mode: 'slow' | 'fast';
    lengths: { min: number; max: number; current: number };
}

function DurationErrorPopup({ open, anchorEl, onMouseEnter, onMouseLeave, onReset, mode, lengths }: IProps) {
    return (
        <Popover
            disableRestoreFocus
            sx={{ pointerEvents: 'none' }}
            slotProps={{ paper: { sx: styles.paper, onMouseEnter, onMouseLeave } }}
            anchorOrigin={{
                vertical: 'center',
                horizontal: -10,
            }}
            transformOrigin={{
                vertical: 'center',
                horizontal: 'right',
            }}
            open={open}
            anchorEl={anchorEl}>
            <Title variant="h4">
                <Capitalized>{mode}</Capitalized>&nbsp;speech
            </Title>
            <CharCountContainer>
                <GrayBodyText>Characters</GrayBodyText>
                <CharCounts>
                    <GrayBodyText>Current</GrayBodyText>
                    <ErrorBodyText>{lengths.current}</ErrorBodyText>
                    <CircleSeparator />
                    <GrayBodyText>Current</GrayBodyText>
                    <SuccessBodyText>
                        {lengths.min}&nbsp;-&nbsp;{lengths.max}
                    </SuccessBodyText>
                </CharCounts>
            </CharCountContainer>
            <ContentText>
                The translated text is {mode === 'fast' ? 'longer' : 'shorter'} than the original.
                <br />
                Please {mode === 'fast' ? 'reduce' : 'increase'} the number of characters for a<br />
                natural speech rhythm.
            </ContentText>
            <ResetButton color="secondary" variant="outlined" onClick={onReset} startIcon={<MaskedIcon src={Restart} size="16px" />}>
                Reset
            </ResetButton>
        </Popover>
    );
}

export default DurationErrorPopup;
